import { costRateValidOn, walkPlan } from '@timelog/pm-shared';
import type { EmployeeLookupItem, PlanNode, RoleRef } from '@timelog/pm-shared';
import type { TFunction } from 'i18next';

// Types only from the hook module — importing its VALUES would reach
// `./editor` and the OIDC session, which is `window` in a node-environment
// unit test (see the note at the top of capture/outline.ts).
import type { RoleOption, RolePricing } from './useRoleOptions';

import { i18n } from '@/i18n';
import { formatAmount, todayIso } from '@/lib/format';

/**
 * The role this person is already allocated at somewhere in THIS plan.
 *
 * A person staffed anywhere on the project carries the role the team bills
 * them at, so the second task they are added to should not ask again.
 * Most-used wins, ties broken by first encountered in plan order. Every stored
 * line names a role (BR-29), so every line is evidence.
 *
 * Iterative (`walkPlan`) — plans hold tens of thousands of nodes (BR-3).
 */
export const roleFromPlan = (
  personTlpId: string,
  plan: readonly PlanNode[],
): RoleRef | undefined => {
  const counts = new Map<string, { role: RoleRef; count: number }>();
  walkPlan(plan as PlanNode[], (node) => {
    for (const allocation of node.allocations) {
      if (allocation.personRef?.tlpId !== personTlpId) continue;
      const key = allocation.roleRef.tlpId;
      const seen = counts.get(key);
      if (seen) seen.count += 1;
      else counts.set(key, { role: allocation.roleRef, count: 1 });
    }
  });

  let best: { role: RoleRef; count: number } | undefined;
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  return best?.role;
};

/**
 * The role a picked person brings with them (D16, person first) — always one
 * of the roles THIS task's contract offers, never anything else. Two signals,
 * strongest first:
 *
 * 1. `defaultRoleServiceId` — the employee's standard hourly rate. Real data
 *    from the fixture; in live mode the prototype stand-in (D21) until
 *    TimeLog exposes the relation.
 * 2. The role this person is ALREADY allocated at on this plan
 *    (`roleFromPlan`) — evidence about the person rather than a guess.
 *
 * Deliberately NOT a signal: the employee's job title or TimeLog RBAC role
 * name (`position`, `roleName`). Those are other dimensions of the person and
 * matching them against rate names by string was wrong when it hit.
 *
 * Every hit is filtered through the offered options: a role the contract does
 * not price is never assigned silently (D16) — undefined leaves the field for
 * the user, which is honest. Nothing is invented: a made-up `tlpId` would be
 * stored in the document.
 */
export const roleOfPerson = (
  person: { tlpId?: string; defaultRoleServiceId?: string },
  roleOptions: readonly RoleOption[],
  plan: readonly PlanNode[] = [],
): RoleRef | undefined => {
  const offered = (serviceId: string | undefined): RoleRef | undefined =>
    serviceId === undefined
      ? undefined
      : roleOptions.find((option) => option.roleRef.tlpId === serviceId)?.roleRef;

  const byDefault = offered(person.defaultRoleServiceId);
  if (byDefault) return byDefault;

  return person.tlpId === undefined ? undefined : offered(roleFromPlan(person.tlpId, plan)?.tlpId);
};

/**
 * Why the role field has nothing to offer, in the user's terms — or null when
 * it does.
 *
 * The picker used to render a lone "Not set" whatever the reason, which reads
 * as a broken field rather than as an upstream one. The default list comes
 * from TimeLog through the proxy and can time out or be switched off; it is
 * also scoped to the caller's legal entity (D18), so an empty one names the
 * entity — the roles may well exist, in another entity. A T&M card is the
 * PM's own list and says so.
 *
 * `t` is passed in so the copy stays assertable; it falls back to the shared
 * instance (the current language) for callers that do not pass one yet.
 */
export const roleCatalogNotice = (
  input: {
    optionCount: number;
    lookupsEnabled: boolean;
    /** The role catalog request failed or timed out. */
    failed: boolean;
    /** Where the roles come from: a T&M contract's card, or the entity's default list. */
    source: 'rate-card' | 'default-list';
    /** The caller's legal entity, once the default list has answered. */
    legalEntityName?: string;
  },
  t: TFunction<'pmPlanningPanel'> = i18n.getFixedT(null, 'pmPlanningPanel'),
): string | null => {
  if (input.optionCount > 0) return null;
  if (input.source === 'rate-card') return t('roleCatalog.RateCardEmpty');
  if (input.failed) return t('roleCatalog.Failed');
  if (!input.lookupsEnabled) return t('roleCatalog.LookupOff');
  const entity = input.legalEntityName?.trim();
  return entity ? t('roleCatalog.NoRatesInEntity', { entity }) : t('roleCatalog.NoRatesForEntity');
};

/**
 * What a person row says under the name: their default role (D21), and under
 * cost-to-cost also their cost price valid today — the figure the line's
 * revenue will derive from (CALC-24), so it has to be traceable where the
 * person is picked. Nothing when neither is known. Shared by the overlay's
 * person field and the timeline's person list, so the two read alike.
 */
export const personSubtitle = (
  item: EmployeeLookupItem,
  pricing: RolePricing | undefined,
  t: TFunction<'pmPlanningPanel'>,
): string | undefined => {
  const cost =
    pricing === 'cost-to-cost' && item.costRates
      ? costRateValidOn(item.costRates, todayIso())
      : undefined;
  const parts = [
    item.defaultRoleName,
    cost === undefined ? undefined : t('shared.CostPerHour', { amount: formatAmount(cost) }),
  ].filter((part): part is string => part !== undefined);
  return parts.length > 0 ? parts.join(' · ') : undefined;
};
